import {colorFromName, colorN, ROBIN16} from '@wandb/weave/common/util/colors';
import _ from 'lodash';

import {
  DefaultResourceConfig,
  FetchEntityProjectsQuery,
  FetchProjectLaunchQueuesQueryResult,
} from '../../generated/graphql';
// TODO: Should the cleanConfig method be moved here?
import {cleanConfig} from './LaunchConfigEditor';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {JobInfo} from './SelectJob';
import {ProjectOption} from './SelectProject';
import {
  GroupedQueueOptions,
  isGroupedQueueOption,
  QueueOption,
} from './SelectQueue';
import {JsonObject, ResourceType} from './utilTypes';

export const PUBLIC_QUEUE_RAMP_KEY = 'gorilla.RampAllowEvalPublicQueue';
export const WANDB_ENTITY_NAME = 'wandb';
export const WANDB_JOBS_PROJECT_NAME = 'jobs';
// name used for triggers that we create for automations not intended to be user-facing
// This is really only for one type of automation now, if we want to create more,
// we should create a prefix for all of them (or actually create a flag + metadata field
// about it being a wandb-owned trigger)
export const WANDB_SYSTEM_AUTOMATION_NAME = `slack-link-artifact`;

export const WANDB_PUBLIC_QUEUE_NAME = 'CoreWeave';

export const isWandbJobsProject = (entityName: string, projectName: string) => {
  return (
    entityName === WANDB_ENTITY_NAME && projectName === WANDB_JOBS_PROJECT_NAME
  );
};

// todo: replace this with actual jobs
const PUBLIC_JOBS = ['Evaluate API-hosted Model', 'Evaluate Model Checkpoint'];

export const isAllowedWandbPublicJob = (
  jobName?: string,
  projectName?: string,
  entityName?: string
) => {
  if (!jobName) {
    return false;
  }
  if (
    PUBLIC_JOBS.includes(jobName) &&
    projectName === WANDB_JOBS_PROJECT_NAME &&
    entityName === WANDB_ENTITY_NAME
  ) {
    return true;
  }
  return false;
};

export const constructQueueOptions = (
  queryResult: FetchProjectLaunchQueuesQueryResult,
  defaultResourceConfigs: Record<string, DefaultResourceConfig>
): QueueOption[] => {
  const queueOptions: QueueOption[] = [];
  const projectName = queryResult?.data?.project?.name ?? '';
  const entityName = queryResult?.data?.project?.entity?.name ?? '';
  const isTeam = queryResult?.data?.project?.entity?.isTeam ?? false;
  const runQueues = queryResult?.data?.project?.runQueues ?? [];
  for (const queue of runQueues) {
    if (queue.name == null || isTeam == null || queue.id == null) {
      continue;
    }
    const defaultResourceConfigID = queue.defaultResourceConfigID || '';
    const drc = defaultResourceConfigs[defaultResourceConfigID] ?? {};
    const resourceType = drc.resource ?? 'unknown';
    const config = drc?.config?.resource_args[resourceType] ?? {};

    const queueOption: QueueOption = {
      isTeam,
      value: queue.id,
      entity: entityName,
      project: projectName,
      queue: queue.name,
      queueID: queue.id,
      isPrivate: queue.access === 'USER',
      defaultResourceConfigID,

      // These have to be populated after configs are loaded
      resourceType,
      config,
    };
    queueOptions.push(queueOption);
  }
  return queueOptions;
};

export const getProjectOptions = (
  data: FetchEntityProjectsQuery
): ProjectOption[] => {
  const options: ProjectOption[] = [];
  const projectEdges = data?.entity?.projects?.edges ?? [];
  for (const projectEdge of projectEdges) {
    const project = projectEdge?.node;
    if (project) {
      options.push({value: project.id, label: project.name});
    }
  }
  return options;
};

export const sortQueues = (queues: QueueOption[]) => {
  queues.sort((a, b) => {
    if (a.queue === 'default') {
      return b.queue === 'default' ? 0 : -1;
    }
    if (b.queue === 'default') {
      return 1;
    }
    return a.queue.toLowerCase() < b.queue.toLowerCase() ? -1 : 1;
  });
};

export const countQueueOptions = (
  queues: QueueOption[] | GroupedQueueOptions[]
) => {
  return isGroupedQueueOption(queues)
    ? (queues as GroupedQueueOptions[]).reduce((acc, queue) => {
        return acc + queue.options.length;
      }, 0)
    : (queues as QueueOption[]).length;
};

export const suggestDefaultQueueForGrouped = (
  options: GroupedQueueOptions[],
  defaultQueueID?: string
): QueueOption | undefined => {
  for (const group of options) {
    if (group.options.length > 0) {
      if (defaultQueueID == null) {
        return group.options[0];
      }
      const defaultQueue = group.options.find(
        option => option.queueID === defaultQueueID
      );
      if (defaultQueue != null) {
        return defaultQueue;
      }
    }
  }
  return;
};

export const parseJobInput = (
  jobName: string | undefined
): JobInfo | undefined => {
  if (jobName == null || jobName === '') {
    return;
  }
  const splitJobInfo = jobName.split('/');
  const jobNameAndAlias = splitJobInfo[splitJobInfo.length - 1].split(':');
  const jobProject =
    splitJobInfo.length > 1 ? splitJobInfo[splitJobInfo.length - 2] : undefined;
  if (jobNameAndAlias.length !== 2) {
    return {
      jobName: jobNameAndAlias[0],
      jobVersion: 'latest',
      jobProject,
    };
  }
  return {
    jobName: jobNameAndAlias[0],
    jobVersion: jobNameAndAlias[1],
    jobProject,
  };
};

export const quoteIfMultiWord = (commandString: string): string => {
  if (commandString.includes(' ')) {
    return '"' + commandString + '"';
  }
  return commandString;
};

export function getDefaultColor(
  defaultColorIndex: number | null | undefined,
  name: string
) {
  return defaultColorIndex != null
    ? colorN(defaultColorIndex, ROBIN16)
    : colorFromName(name);
}

export const isLocalResourceType = (resourceType: ResourceType): boolean => {
  return ['local-container', 'local-process'].includes(resourceType);
};

// Run configs as returned by the server have additional internal information.
export const extractConfig = (config: JsonObject): JsonObject => {
  return _.omit(cleanConfig(config), '_wandb');
};

export const padLeft = (
  str: string,
  length: number,
  character: string
): string => {
  let result = str;
  for (let i = result.length; i < length; i++) {
    result = character + result;
  }
  return result;
};
